import cloneDeep from "lodash-es/cloneDeep";
import isArray from "lodash-es/isArray";
import lodash_isEmpty from "lodash-es/isEmpty";
import isFunction from "lodash-es/isFunction";
import isNumber from "lodash-es/isNumber";
import isObject from "lodash-es/isObject";
import isString from "lodash-es/isString";
import mergeWith from "lodash-es/mergeWith";

// TODO (Noah, 2026-02-20): These re-exports are intentional - replo-utils/lib/misc is a
// convenience module that re-exports commonly used lodash utilities for consumers. We
// should consider removing these in the future
// eslint-disable-next-line replo/no-export-from -- (Noah, 2026-07-20): public package API re-export.
export { isFunction, isNumber, isString };

/**
 * Checks if a value is neither `null` nor `undefined`.
 */
export function isNotNullish<ValueType>(
  value: ValueType,
): value is Exclude<ValueType, null | undefined> {
  return value !== null && value !== undefined;
}

/**
 * Checks if a value is either `null` or `undefined`.
 */
export function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

export function getFromRecordOrNull<RecordKey extends PropertyKey, RecordValue>(
  record: Record<RecordKey, RecordValue> | null | undefined,
  key: RecordKey | undefined | null,
) {
  return key ? record?.[key] ?? null : null;
}

export function isPrimitive(
  value: unknown,
): value is string | number | boolean {
  const type = typeof value;
  return ["string", "number", "boolean"].includes(type);
}

type StructureRecord<UnionType extends { type: string }, ResultType> = {
  [MemberType in UnionType as MemberType["type"]]:
    | ResultType
    | ((value: MemberType) => ResultType);
};

// Note: This is the StructureRecord analog for bare string unions, where the
// value is its own discriminant
type StructureRecordBareString<UnionType extends string, ResultType> = {
  [MemberType in UnionType]: ResultType | ((value: MemberType) => ResultType);
};

// Note: This is a more generic version of StructureRecord that works with any discriminant key
type StructureRecordGeneric<
  UnionType extends Record<KeyType, string>,
  ResultType,
  KeyType extends keyof UnionType,
> = {
  [MemberType in UnionType as MemberType[KeyType]]:
    | ResultType
    | ((value: MemberType) => ResultType);
};

/**
 * @returns A function which can be used to create an exhaustive switch over all
 * types of a discriminated union, based on a given key, or over all members of
 * a bare string union.
 *
 * When no key is provided, it defaults to using "type" as the discriminant.
 * When the value is a bare string union, the value is its own discriminant.
 *
 * Examples:
 *
 * With default "type" discriminant:
 * ```
 * type OneOrTwo = { type: "one", numberValue: number } |
 * { type: "two", stringValue: string }
 *
 * let oneOrTwo = ...
 * let result = exhaustiveSwitch(oneOrTwo)({
 * one: (op) => `${op.numberValue + 1}`, // Typescript knows numberValue is a number
 * two: (op) => op.stringValue + "hello", // Typescript knows stringValue is a string
 * }) // type of result is inferred to be string
 * ```
 *
 * With custom discriminant key:
 * ```
 * type OneOrTwo = { variant: "one", numberValue: number } |
 * { variant: "two", stringValue: string }
 *
 * let oneOrTwo = ...
 * let result = exhaustiveSwitch(oneOrTwo, "variant")({
 * one: (op) => `${op.numberValue + 1}`, // Typescript knows numberValue is a number
 * two: (op) => op.stringValue + "hello", // Typescript knows stringValue is a string
 * }) // type of result is inferred to be string
 * ```
 *
 * With a bare string union:
 * ```
 * type OneOrTwo = "one" | "two"
 *
 * let oneOrTwo = ...
 * let result = exhaustiveSwitch(oneOrTwo)({
 * one: 1, // cases can be plain values...
 * two: () => 1 + 1, // ...or functions, evaluated lazily
 * }) // type of result is inferred to be number
 * ```
 *
 * Typescript will error if you forget a case, or if you assume the wrong type
 * for a case value. The switch also returns the value you return from the
 * function mapping, so it works well for "extension" methods where you want to
 * map over all discriminated union cases and do a different thing for each.
 *
 * Works well for many of our types, like ComponentActionType, Action, etc
 */
// Overload for bare string unions, where the value is its own discriminant
export function exhaustiveSwitch<UnionType extends string>(
  value: UnionType,
): <ResultType>(
  cases: StructureRecordBareString<UnionType, ResultType>,
) => ResultType;
// Overload for the default case with "type" discriminant
export function exhaustiveSwitch<UnionType extends { type: string }>(
  value: UnionType,
): <ResultType>(cases: StructureRecord<UnionType, ResultType>) => ResultType;
// Overload for custom discriminant key
export function exhaustiveSwitch<
  UnionType extends Record<KeyType, string>,
  KeyType extends keyof UnionType,
>(
  value: UnionType,
  key: KeyType,
): <ResultType>(
  cases: StructureRecordGeneric<UnionType, ResultType, KeyType>,
) => ResultType;
// Implementation
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function exhaustiveSwitch(value: any, discriminantKey: any = "type") {
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  return (cases: any): any => {
    const discriminant =
      typeof value === "string" ? value : value[discriminantKey];
    const caseHandler = cases[discriminant];
    if (!isFunction(caseHandler)) {
      return caseHandler;
    }
    return caseHandler(value);
  };
}

type EmptyObject<ValueType> = { [KeyType in keyof ValueType]?: never };
type EmptyObjectOf<ValueType> =
  EmptyObject<ValueType> extends ValueType ? EmptyObject<ValueType> : never;

/**
 * The same as `isEmpty` from lodash, but with a few additional overloads for
 * stronger type-checking on empty arrays.
 */
export function isEmpty(value: string): value is "";
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function isEmpty<ValueType extends any[]>(
  value: ValueType | null | undefined,
): value is (ValueType & []) | null | undefined;
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function isEmpty<ValueType extends ArrayLike<any>>(
  value: ValueType | null | undefined,
): value is (ValueType & { length: 0 }) | null | undefined;
export function isEmpty(
  // oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
  value: Map<any, any> | Set<any> | ArrayLike<any> | null | undefined,
): boolean;
export function isEmpty(value: object): boolean;
export function isEmpty<ValueType extends object>(
  value: ValueType | null | undefined,
): value is EmptyObjectOf<ValueType> | null | undefined;
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function isEmpty(value?: any): boolean;

// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function isEmpty(value: any) {
  return lodash_isEmpty(value);
}

export function noop() {}

export function preventDefault(event: { preventDefault(): void }) {
  event.preventDefault();
}

/**
 * Copyright (c) 2019 Alexander Reardon
 * https://github.com/alexreardon/tiny-warning/blob/master/src/index.js
 */
export function warning(
  condition: unknown,
  message: string | (() => string),
  options?: { severity: "log" | "warn" | "error" },
) {
  if (condition) {
    return;
  }

  const prefix = "[REPLO]";
  const provided = isFunction(message) ? message() : message;
  const text = provided ? `${prefix}: ${provided}` : prefix;
  const severity = options?.severity || "warn";
  console[severity](text);
  // Throwing an error and catching it immediately to improve debugging. A
  // consumer can use 'pause on caught exceptions'
  // https://github.com/facebook/react/issues/4216
  try {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(text);
  } catch {
    // allow empty catch
  }
}

/**
 *
 * Copyright (c) 2019 Alexander Reardon
 * https://github.com/alexreardon/tiny-warning/blob/master/src/index.js
 */
export function invariant(
  condition: unknown,
  message: string | (() => string),
  opts?: { stripErrorMessage: unknown },
): asserts condition {
  const prefix = "[REPLO] Invariant failed";
  if (condition) {
    return;
  }

  if (opts?.stripErrorMessage) {
    // eslint-disable-next-line replo/no-raw-error-throw
    throw new Error(prefix);
  }

  const provided = isFunction(message) ? message() : message;
  const text = provided ? `${prefix}: ${provided}` : prefix;
  // eslint-disable-next-line replo/no-raw-error-throw
  throw new Error(text);
}

export const mergeReplacingObjects = (first: Object, second: Object) => {
  mergeWith(first, second, (_first, value) => {
    return isObject(value) && isEmpty(value) ? value : undefined;
  });
};

export const mergeReplacingArrays = (first: Object, second: Object) => {
  mergeWith(first, second, (_first, value) => {
    return isArray(value) ? value : undefined;
  });
};

export const deepCloneAndMergeReplacingArrays = <ValueType>(
  source: ValueType,
  ...toMerge: Object[]
): ValueType => {
  const copy = cloneDeep(source);
  for (const object of toMerge) {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    mergeReplacingArrays(copy as Object, object);
  }
  return copy;
};

/**
 * hasOwnProperty with a type assertion. If this returns true, typescript knows that `obj`
 * has a property called `prop` from that point forward
 * https://fettblog.eu/typescript-hasownproperty/
 */
export function hasOwnProperty<
  ObjectType extends {} | null | undefined,
  KeyType extends PropertyKey,
>(
  obj: ObjectType,
  prop: KeyType,
): obj is ObjectType & Record<KeyType, unknown> {
  return obj?.hasOwnProperty(prop) || false;
}

/**
 * Given a string | number param, converts it to a string, unless it's nullish
 * in which case it just returns it. Useful for times when you have string | number
 * | nullish types, like when selecting the current value of a CSS property in a right
 * bar modifier
 */
export function coerceNumberToString(value: string | number): string;
export function coerceNumberToString(
  value: string | number | null | undefined,
): string | null | undefined;
export function coerceNumberToString(
  value: string | number | undefined,
): string | undefined;
export function coerceNumberToString(
  value: string | number | null,
): string | null;
// oxlint-disable-next-line typescript/no-explicit-any -- legacy any, may or may not be needed
export function coerceNumberToString(value: any): any {
  if (isNotNullish(value)) {
    return String(value);
  }
  return value;
}

/**
 * Formats a file size in bytes to a human readable string with appropriate
 * units. Rounds to 0 decimal places unless otherwise specified and uses
 * standard binary units (KB, MB, etc).
 */
export function formatFileSize(byteCount: number, decimalPlaces = 0): string {
  if (byteCount === 0) {
    return "0 Bytes";
  }

  const bytesPerUnit = 1024;
  const binaryUnits = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  // Calculate which unit to use based on the size
  const unitIndex = Math.floor(Math.log(byteCount) / Math.log(bytesPerUnit));

  // Convert to the appropriate unit and round to decimal places
  const sizeInUnit = byteCount / Math.pow(bytesPerUnit, unitIndex);
  const formattedSize = Number.parseFloat(sizeInUnit.toFixed(decimalPlaces));

  return `${formattedSize} ${binaryUnits[unitIndex]}`;
}

export const sleep = async (ms: number) => {
  return await new Promise((resolve) => setTimeout(resolve, ms));
};

export function sleepWithSignal({
  delayMs,
  signal,
}: {
  delayMs: number;
  signal: AbortSignal | null;
}) {
  if (signal === null) {
    return sleep(delayMs);
  }
  signal.throwIfAborted();
  return new Promise<void>((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(signal.reason);
    };
    const timeoutId = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export function errorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }
  if (
    typeof error === "object" &&
    error &&
    "message" in error &&
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    typeof (error as { message: unknown }).message === "string"
  ) {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    return (error as { message: string }).message;
  }
  return String(error);
}
